const GENERIC_PLAN_TOKENS = new Set([
  'plan',
  'plans',
  'product',
  'products',
  'insurance',
  'life',
  'term',
]);

/**
 * Liferay object fields on product pincode master that gate Super Suraksha Plus / Elite.
 * Add more keys here when new term variants use the same master pattern (boolean = serviceable).
 */
export const TERM_PLAN_PINCODE_FIELD_KEYS = Object.freeze({
  plus: 'superSurakshaPlus',
  elite: 'superSurakshaElite',
});

const tokenizeText = (value) =>
  String(value ?? '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .map((token) => token.trim())
    .filter(Boolean)
    .filter((token) => !GENERIC_PLAN_TOKENS.has(token));

/**
 * Reads Plus/Elite serviceability from a pincode master row.
 * Missing field → treated as allowed (backward compatible with older rows).
 * Explicit `false` → not serviceable at this pincode.
 */
export const readTermPlanPincodeAvailability = (pincodeRecord) => {
  const plusKey = TERM_PLAN_PINCODE_FIELD_KEYS.plus;
  const eliteKey = TERM_PLAN_PINCODE_FIELD_KEYS.elite;
  const plusRaw = pincodeRecord?.[plusKey];
  const eliteRaw = pincodeRecord?.[eliteKey];
  return {
    plusAllowed: plusRaw !== false,
    eliteAllowed: eliteRaw !== false,
  };
};

export const findMatchingPincodePlanFlag = (
  pincodeRecord,
  assignedProductName,
) => {
  if (!pincodeRecord || typeof pincodeRecord !== 'object') return null;

  const productTokens = tokenizeText(assignedProductName);
  if (productTokens.length === 0) return null;

  const booleanEntries = Object.entries(pincodeRecord).filter(
    ([, value]) => typeof value === 'boolean',
  );
  if (booleanEntries.length === 0) return null;

  return booleanEntries.reduce((bestMatch, [key, value]) => {
    const keyTokens = tokenizeText(key);
    const overlapCount = productTokens.filter((token) =>
      keyTokens.includes(token),
    ).length;

    if (overlapCount === 0) return bestMatch;

    const score = overlapCount * 100 + keyTokens.length;
    if (!bestMatch || score > bestMatch.score) {
      return { key, value, score };
    }

    return bestMatch;
  }, null);
};

export const evaluatePincodeServiceability = (
  pincodeRecord,
  assignedProductName,
) => {
  if (!pincodeRecord || typeof pincodeRecord !== 'object') {
    return {
      matchedPlanFlag: null,
      isServiceable: false,
      shouldBlock: true,
      reason: 'PINCODE_NOT_FOUND',
    };
  }

  const matchedPlanFlag = findMatchingPincodePlanFlag(
    pincodeRecord,
    assignedProductName,
  );

  return {
    matchedPlanFlag: matchedPlanFlag?.key ?? null,
    isServiceable:
      typeof matchedPlanFlag?.value === 'boolean' ? matchedPlanFlag.value : null,
    shouldBlock: matchedPlanFlag?.value === false,
    reason: matchedPlanFlag?.value === false ? 'PLAN_NOT_SERVICEABLE' : null,
  };
};

/**
 * Lead submit: allow journey if at least one term plan (Plus or Elite) is serviceable at pincode.
 * Does not hard-block when assigned Elite but only Plus is true — Quote will hide Elite.
 */
export const evaluateLeadPincodeTermAccess = (pincodeRecord) => {
  if (!pincodeRecord || typeof pincodeRecord !== 'object') {
    return {
      shouldBlockLead: true,
      reason: 'PINCODE_NOT_FOUND',
      availability: null,
    };
  }

  const availability = readTermPlanPincodeAvailability(pincodeRecord);
  if (!availability.plusAllowed && !availability.eliteAllowed) {
    return {
      shouldBlockLead: true,
      reason: 'NO_TERM_PLAN_AT_PINCODE',
      availability,
    };
  }

  return {
    shouldBlockLead: false,
    reason: null,
    availability,
  };
};
